/**
 * Maps a telemetry field's backend unit name to a compact display form.
 *
 * The API has been observed sending spelled-out unit names ("kilowatt", "percent",
 * "watt", "degree_Celsius") rather than symbols — this is a temporary frontend
 * workaround. The backend team has been asked to send the display form directly;
 * once that ships, this mapping becomes a no-op for those units and can be trimmed
 * down (or removed) rather than ripped out wholesale, since unknown/future units
 * still need a safe pass-through.
 *
 * Keys are normalized (lowercased, underscores → spaces) so "degree_Celsius" and
 * "degree celsius" match the same entry. Unrecognized units pass through unchanged.
 */
const UNIT_DISPLAY_MAP: Record<string, string> = {
  kilowatt: 'kW',
  kilowatts: 'kW',
  'kilowatt hour': 'kWh',
  'kilowatt hours': 'kWh',
  watt: 'W',
  watts: 'W',
  'watt hour': 'Wh',
  'watt hours': 'Wh',
  kilovolt: 'kV',
  volt: 'V',
  volts: 'V',
  ampere: 'A',
  amperes: 'A',
  amp: 'A',
  amps: 'A',
  hertz: 'Hz',
  percent: '%',
  pct: '%',
  'degree celsius': '°C',
  celsius: '°C',
  'degree fahrenheit': '°F',
  fahrenheit: '°F',
  ohm: 'Ω',
  ohms: 'Ω',
};

function normalizeUnitKey(unit: string): string {
  return unit.trim().toLowerCase().replace(/_/g, ' ');
}

export function toDisplayUnit(unit: string): string;
export function toDisplayUnit(unit: string | null): string | null;
export function toDisplayUnit(unit: string | null): string | null {
  if (unit === null) return null;
  return UNIT_DISPLAY_MAP[normalizeUnitKey(unit)] ?? unit;
}
